import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import useSWR from "swr";
import toast from "react-hot-toast";
import { getFetcher, postFetcher } from "../../../../services/api";
import { transporterValidation } from "../../../../services/validation/transporter.validation";
import { isResponseErrorObject } from "../../../../services/helpers";
import ControlledInput from "../../../elements/ControlledInput";
import ControlledReactSelect from "../../../elements/ControlledReactSelect";
import DataPICSelector from "../report/ModalDataPIC";
import useListPic from "../../../../hooks/useListPic";

/*
 * Dua pilihan tetap di puncak daftar transporter.
 *
 * Pengiriman lewat kapal atau pipa tidak memakai perusahaan pengangkut, tetapi
 * tetap harus tercatat. Selama ini pengguna mengetiknya sendiri lewat "Tambah
 * Manual" dan ejaannya berserakan - "PIPA", "Pipa", "Via Pipa", "Jalur
 * Perpipaan", "MELALUI PIPA", "Distribusi Via Kapal" - sehingga tidak bisa
 * dikelompokkan. Nilainya sengaja bukan UUID agar tidak mungkin bentrok dengan
 * id perusahaan, dan dibuang sebelum data dikirim ke server.
 */
const MODA_TANPA_PERUSAHAAN = [
  { value: "MODA_KAPAL", label: "KAPAL" },
  { value: "MODA_PIPA", label: "PIPA" },
];

/**
 * Reusable Modal Component for Adding Transporter Company
 *
 * @param {boolean} isOpen - Modal open state
 * @param {function} onOpenChange - Handler for modal open/close state change
 * @param {function} onClose - Handler for closing modal
 * @param {string} selectedCompany - Selected company ID
 * @param {function} onSuccess - Callback function after successful creation (optional)
 * @param {function} mutateTransporter - SWR mutate function to refresh transporter list (optional)
 */
export default function AddTransporterModal({ isOpen, onOpenChange, onClose, selectedCompany, onSuccess, mutateTransporter }) {
  const [isManualMode, setIsManualMode] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({ resolver: yupResolver(transporterValidation) });

  const provinceId = watch("provinceId");
  const regencyId = watch("regencyId");
  const districtId = watch("districtId");
  const selectedTransporterId = watch("transporterId");

  // Fetch location data
  const { data: province } = useSWR("/api/location/provinces", getFetcher);
  const { data: regency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}` : null, getFetcher);
  const { data: district } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}` : null, getFetcher);
  const { data: village } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}` : null, getFetcher);

  /*
   * companyId HARUS ada di state react-hook-form, bukan cuma di DOM.
   *
   * Dulu kolom ini hanya berupa <Input type="hidden" {...register("companyId")}
   * value={selectedCompany} />. Prop value tidak pernah memicu event perubahan,
   * jadi react-hook-form tidak pernah mencatat nilainya. Selama form belum
   * pernah di-reset hal itu tidak terasa - RHF masih membaca nilai dari DOM -
   * tetapi handleClose() me-reset companyId menjadi string kosong, sehingga
   * pada percobaan KEDUA dan seterusnya validasi yup gagal dengan "Harus diisi"
   * pada kolom yang tak kasat mata. Tombol Tambah pun tampak mati: tidak ada
   * permintaan yang dikirim dan tidak ada pesan apa pun.
   */
  useEffect(() => {
    setValue("companyId", selectedCompany ?? "");
  }, [selectedCompany, isOpen, setValue]);

  // Fetch PIC data
  const { dataPIC, isLoadingDataPIC, mutatePIC } = useListPic({ companyId: selectedCompany });

  // Fetch transporter companies for auto-fill
  const { data: transporterCompanies, isLoading: isLoadingTransporterCompanies } = useSWR("/api/company/get-transporters", getFetcher);

  // Transform data transporter companies untuk react-select
  const transporterCompanyOptions = useMemo(() => {
    const daftarPerusahaan = (transporterCompanies?.data ?? []).map((company) => ({
      value: company.id,
      label: company.name,
      data: company,
    }));
    return [...MODA_TANPA_PERUSAHAAN, ...daftarPerusahaan];
  }, [transporterCompanies]);

  // Effect untuk auto-fill data ketika transporter dipilih
  useEffect(() => {
    if (selectedTransporterId && transporterCompanies?.data) {
      const selectedCompany = transporterCompanies.data.find((company) => company.id === selectedTransporterId);
      if (selectedCompany) {
        setValue("namaTransporter", selectedCompany.name);
        setValue("alamat", selectedCompany.alamatKantor || "");
        setValue("email", selectedCompany.emailKantor || "");
        setValue("telepon", selectedCompany.telpKantor || "");
        setValue("fax", selectedCompany.faxKantor || "");
        setValue("latitude", selectedCompany.latitude || "");
        setValue("longitude", selectedCompany.longitude || "");
        setValue("provinceId", selectedCompany.provinceId || "");
        setValue("regencyId", selectedCompany.regencyId || "");
        setValue("districtId", selectedCompany.districtId || "");
        setValue("villageId", selectedCompany.villageId || "");
      }
    }
  }, [selectedTransporterId, transporterCompanies, setValue]);

  /*
   * KAPAL dan PIPA tidak punya data perusahaan untuk disalin, jadi hanya
   * namanya yang diisi dan kolom lain dikosongkan - kalau tidak, alamat dan
   * koordinat perusahaan yang sempat dipilih sebelumnya akan tertinggal di
   * bawah nama "KAPAL". Sisanya diisi pengguna seperti pada mode manual.
   *
   * Dikerjakan lewat onOptionChange, bukan useEffect, supaya hanya jalan saat
   * pengguna benar-benar memilih dan tidak ikut terpicu saat daftar transporter
   * disegarkan ulang oleh SWR.
   */
  function handlePilihTransporter(option) {
    const moda = MODA_TANPA_PERUSAHAAN.find((item) => item.value === option?.value);
    if (!moda) return;

    setValue("namaTransporter", moda.label);
    setValue("alamat", "");
    setValue("email", "");
    setValue("telepon", "");
    setValue("fax", "");
    setValue("latitude", "");
    setValue("longitude", "");
    setValue("provinceId", "");
    setValue("regencyId", "");
    setValue("districtId", "");
    setValue("villageId", "");
  }

  // Handler untuk toggle manual mode
  function handleToggleManualMode(checked) {
    setIsManualMode(checked);
    if (checked) {
      // Clear dropdown selection when switching to manual mode
      setValue("transporterId", "");
    } else {
      // Clear all fields when switching back to selection mode
      setValue("namaTransporter", "");
      setValue("alamat", "");
      setValue("email", "");
      setValue("telepon", "");
      setValue("fax", "");
      setValue("latitude", "");
      setValue("longitude", "");
      setValue("provinceId", "");
      setValue("regencyId", "");
      setValue("districtId", "");
      setValue("villageId", "");
    }
  }

  function handleClose() {
    setIsManualMode(false);
    reset({
      // Bukan string kosong: perusahaan pemilik tidak berubah saat form ditutup,
      // dan mengosongkannya membuat percobaan berikutnya gagal validasi diam-diam.
      companyId: selectedCompany ?? "",
      namaTransporter: "",
      alamat: "",
      email: "",
      telepon: "",
      fax: "",
      latitude: "",
      longitude: "",
      provinceId: "",
      regencyId: "",
      districtId: "",
      villageId: "",
      dataPICIds: [],
      transporterId: "",
    });
    onClose();
  }

  async function onSubmitForm(formData) {
    try {
      // Validation: Cek berdasarkan mode
      if (!isManualMode && !formData.transporterId) {
        toast.error("Pilih perusahaan transporter dari dropdown");
        return;
      }

      if (isManualMode && !formData.namaTransporter) {
        toast.error("Nama transporter wajib diisi");
        return;
      }

      // Remove transporterId from data to send
      const { transporterId, ...dataToSend } = formData;

      const response = await postFetcher("/api/company/create-transporter", dataToSend);

      // Mutate transporter list if function provided
      if (mutateTransporter) {
        mutateTransporter();
      }

      toast.success("Perusahaan transporter berhasil ditambah!");

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(response);
      }

      handleClose();
    } catch (error) {
      isResponseErrorObject(error.response?.data?.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response?.data?.message || "Gagal menambahkan transporter");
    }
  }

  /*
   * Kegagalan validasi harus terlihat.
   *
   * Sebelumnya tombol Tambah diam saja ketika ada isian yang tidak lolos yup:
   * beberapa kolom tidak punya tempat menampilkan pesan error - companyId
   * tersembunyi, dataPICIds dirender komponen lain - jadi dari sisi pengguna
   * tombolnya seperti tidak berfungsi.
   */
  function onInvalidForm(errorsValidasi) {
    console.error("Validasi tambah transporter gagal:", errorsValidasi);

    const pesan = Object.entries(errorsValidasi)
      .map(([kolom, error]) => `${kolom}: ${error?.message ?? "tidak valid"}`)
      .slice(0, 4);

    toast.error(pesan.length > 0 ? `Periksa isian - ${pesan.join("; ")}` : "Ada isian yang belum benar");
  }

  return (
    <Modal scrollBehavior="inside" size="3xl" isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleClose} isDismissable={false} isKeyboardDismissDisabled={false}>
      <ModalContent>
        {(onCloseModal) => (
          <>
            <ModalHeader>Tambah Perusahaan Transporter</ModalHeader>
            <ModalBody>
              <form id="add-transporter-form" onSubmit={handleSubmit(onSubmitForm, onInvalidForm)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <div className="col-span-2">
                    <Checkbox isSelected={isManualMode} onValueChange={handleToggleManualMode} size="sm">
                      Tambah Manual (Isi data secara manual tanpa memilih dari daftar)
                    </Checkbox>
                  </div>

                  {!isManualMode && (
                    <ControlledReactSelect
                      control={control}
                      name="transporterId"
                      label="Pilih Perusahaan Transporter"
                      placeholder="Pilih perusahaan transporter yang sudah ada..."
                      options={transporterCompanyOptions}
                      isLoading={isLoadingTransporterCompanies}
                      onOptionChange={handlePilihTransporter}
                      menuPosition="absolute"
                    />
                  )}

                  <ControlledInput label="Nama Transporter" name="namaTransporter" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledInput label="Alamat" name="alamat" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledInput label="Email" name="email" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledInput label="Telepon" name="telepon" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledInput label="Fax" name="fax" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledInput label="Latitude" name="latitude" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledInput label="Longitude" name="longitude" type="text" isRequired={true} control={control} isDisabled={!isManualMode && !selectedTransporterId} />

                  <ControlledReactSelect
                    control={control}
                    label="Provinsi"
                    name="provinceId"
                    isRequired={true}
                    options={province?.map((item) => ({ value: item.id, label: item.name }))}
                    placeholder="Pilih..."
                    isDisabled={!isManualMode && !selectedTransporterId}
                  />

                  <ControlledReactSelect
                    control={control}
                    label="Kabupaten"
                    name="regencyId"
                    isRequired={true}
                    options={regency?.map((item) => ({ value: item.id, label: item.name }))}
                    placeholder="Pilih..."
                    isDisabled={!isManualMode && !selectedTransporterId}
                  />

                  <ControlledReactSelect
                    control={control}
                    label="Kecamatan"
                    name="districtId"
                    isRequired={true}
                    options={district?.map((item) => ({ value: item.id, label: item.name }))}
                    placeholder="Pilih..."
                    isDisabled={!isManualMode && !selectedTransporterId}
                  />

                  <ControlledReactSelect
                    control={control}
                    label="Kelurahan"
                    name="villageId"
                    isRequired={true}
                    options={village?.map((item) => ({ value: item.id, label: item.name }))}
                    placeholder="Pilih..."
                    isDisabled={!isManualMode && !selectedTransporterId}
                  />

                  <div className="col-span-2">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <ControlledReactSelect control={control} label="Data PIC" name="dataPICIds" isLoading={isLoadingDataPIC} options={dataPIC?.map((item) => ({ value: item.id, label: item.namaPIC }))} isMulti={true} />
                      </div>
                      <DataPICSelector type="transporter" mutatePIC={mutatePIC} selectedCompany={selectedCompany} />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" form="add-transporter-form" color="primary">
                    Tambah
                  </Button>
                  <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onCloseModal}>
                    Batal
                  </Button>
                </div>
              </form>
            </ModalBody>
            <ModalFooter></ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
